import { useEffect, useState } from "react";
import hollandTest from "@helpers/data/hollandTest.json";
import {
  buildHollandPairResultPayload,
  computeHollandPairScores,
} from "../../helpers/hollandScores";
import { saveHollandResults } from "../../helpers/hollandStorage";
import styles from "./styles/hollandPairTest.module.css";

const TYPE_THEME = {
  Р: styles.themeR,
  И: styles.themeI,
  А: styles.themeA,
  С: styles.themeS,
  П: styles.themeP,
  К: styles.themeK,
};

export default function HollandPairTest({
  compact = false,
  onComplete,
  initialAnswers = null,
}) {
  const questions = hollandTest.questions;
  const totalQuestions = questions.length;

  const [phase, setPhase] = useState("intro");
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(initialAnswers || {});
  const [justSelected, setJustSelected] = useState(null);

  useEffect(() => {
    if (!justSelected) return undefined;
    const timer = window.setTimeout(() => {
      setJustSelected(null);
      if (step < totalQuestions - 1) {
        setStep((value) => value + 1);
      } else {
        setPhase("results");
      }
    }, 280);
    return () => window.clearTimeout(timer);
  }, [justSelected, step, totalQuestions]);

  const answeredCount = Object.keys(answers).length;
  const progressPercent = Math.round((answeredCount / totalQuestions) * 100);

  const handleSelect = (choice) => {
    const question = questions[step];
    if (!question || justSelected) return;

    setAnswers((prev) => ({
      ...prev,
      [question.id]: choice,
    }));
    setJustSelected(choice);
  };

  const goPrev = () => {
    if (phase === "results") {
      setPhase("test");
      setStep(totalQuestions - 1);
      return;
    }
    if (step === 0) {
      setPhase("intro");
      return;
    }
    setStep((value) => Math.max(0, value - 1));
  };

  const handleComplete = () => {
    const payload = buildHollandPairResultPayload(answers, questions);
    saveHollandResults(payload);
    onComplete?.(payload);
  };

  const renderIntro = () => (
    <div className={styles.intro}>
      <p className={styles.kicker}>{hollandTest.author}</p>
      <h2 className={styles.title}>{hollandTest.testTitle}</h2>
      {!compact ? (
        <p className={styles.description}>{hollandTest.testDescription}</p>
      ) : null}
      <p className={styles.instruction}>{hollandTest.instruction}</p>
      <button
        type="button"
        className={styles.primaryBtn}
        onClick={() => setPhase("test")}
      >
        Начать тест · 42 шага
      </button>
    </div>
  );

  const renderQuestion = () => {
    const question = questions[step];
    const currentAnswer = answers[question.id];

    return (
      <div className={styles.question}>
        <div className={styles.progressMeta}>
          <span>
            Шаг {step + 1} из {totalQuestions}
          </span>
          <span>{progressPercent}%</span>
        </div>
        <div className={styles.progressTrack} aria-hidden="true">
          <div
            className={styles.progressFill}
            style={{ width: `${((step + 1) / totalQuestions) * 100}%` }}
          />
        </div>

        <p className={styles.prompt}>Какая профессия вам ближе?</p>

        <div className={styles.cards}>
          {["a", "b"].map((choice) => {
            const option = question[choice];
            const selected = currentAnswer === choice;
            const flash = justSelected === choice;

            return (
              <button
                key={choice}
                type="button"
                className={[
                  styles.card,
                  TYPE_THEME[option.type],
                  selected ? styles.cardSelected : "",
                  flash ? styles.cardFlash : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => handleSelect(choice)}
                disabled={Boolean(justSelected)}
              >
                <span className={styles.cardLetter}>
                  {choice === "a" ? "А" : "Б"}
                </span>
                <span className={styles.cardLabel}>{option.label}</span>
                <span className={styles.cardType}>
                  {hollandTest.typeLabels[option.type]}
                </span>
              </button>
            );
          })}
        </div>

        <div className={styles.nav}>
          <button type="button" className={styles.navBtn} onClick={goPrev}>
            Назад
          </button>
          {currentAnswer && !justSelected ? (
            <button
              type="button"
              className={styles.navBtnPrimary}
              onClick={() => {
                if (step < totalQuestions - 1) {
                  setStep((value) => value + 1);
                } else {
                  setPhase("results");
                }
              }}
            >
              {step < totalQuestions - 1 ? "Далее" : "К результатам"}
            </button>
          ) : (
            <span className={styles.hint}>Выберите одну из карточек</span>
          )}
        </div>
      </div>
    );
  };

  const renderResults = () => {
    const { scores, hollandCode, sortedTypes } = computeHollandPairScores(
      answers,
      questions
    );
    const maxScore = Math.max(
      ...Object.values(scores).map((item) => item.total),
      1
    );

    return (
      <div className={styles.results}>
        <p className={styles.kicker}>Результаты</p>
        <h2 className={styles.title}>Ваш код Холланда</h2>
        <p className={styles.code}>{hollandCode || "—"}</p>
        <p className={styles.instruction}>{hollandTest.scoring.instructions}</p>

        <div className={styles.scoreList}>
          {sortedTypes.map((type, index) => {
            const info = hollandTest.typeDescriptions[type];
            const total = scores[type]?.total || 0;
            const width = `${Math.round((total / maxScore) * 100)}%`;

            return (
              <div key={type} className={`${styles.scoreCard} ${TYPE_THEME[type]}`}>
                <div className={styles.scoreHead}>
                  <strong>
                    {index + 1}. {type} — {info?.title || type}
                  </strong>
                  <span>{total}</span>
                </div>
                <div className={styles.scoreBarTrack}>
                  <div className={styles.scoreBarFill} style={{ width }} />
                </div>
                {info?.description && !compact ? (
                  <p className={styles.scoreDesc}>{info.description}</p>
                ) : null}
              </div>
            );
          })}
        </div>

        <div className={styles.nav}>
          <button type="button" className={styles.navBtn} onClick={goPrev}>
            Назад
          </button>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={handleComplete}
          >
            Сохранить результаты
          </button>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`${styles.wizard} ${compact ? styles.wizardCompact : ""}`}
    >
      {phase === "intro" ? renderIntro() : null}
      {phase === "test" ? renderQuestion() : null}
      {phase === "results" ? renderResults() : null}
    </div>
  );
}
